import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';

class ProductRecommendations extends Component {
  #intersectionObserver = new IntersectionObserver((entries, observer) => {
    if (!entries[0]?.isIntersecting) return;
    observer.disconnect();
    this.#loadRecommendations();
  }, { rootMargin: '0px 0px 1200px 0px' });

  #mutationObserver = new MutationObserver((mutations) => {
    if (!mutations.some((mutation) => mutation.target === this)) return;
    this.dataset.recommendationsPerformed = 'false';
    this.classList.remove('hidden');
    delete this.dataset.error;
    this.#loadRecommendations();
  });

  #cachedRecommendations = new Map();
  #activeFetch = null;
  #pendingUrl = null;
  #requestVersion = 0;
  #frame = null;
  #idle = null;
  #timer = null;

  connectedCallback() {
    super.connectedCallback();
    if (this.dataset.recommendationsPerformed !== 'true') {
      this.#intersectionObserver.observe(this);
      if (this.dataset.intent === 'complementary') this.#schedulePrefetch();
    }
    this.#mutationObserver.observe(this, {
      attributes: true,
      attributeFilter: ['data-product-id', 'data-section-id', 'data-intent', 'data-url'],
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
    if (this.#frame !== null) cancelAnimationFrame(this.#frame);
    if (this.#idle !== null) window.cancelIdleCallback(this.#idle);
    if (this.#timer !== null) clearTimeout(this.#timer);
    this.#frame = this.#idle = this.#timer = null;
    this.#activeFetch?.abort();
    this.#activeFetch = null;
    this.#pendingUrl = null;
    this.#requestVersion += 1;
  }

  #schedulePrefetch() {
    this.#frame = requestAnimationFrame(() => {
      this.#frame = requestAnimationFrame(() => {
        this.#frame = null;
        const load = () => {
          this.#idle = this.#timer = null;
          if (this.isConnected) this.#loadRecommendations('low');
        };
        if (typeof window.requestIdleCallback === 'function') {
          this.#idle = window.requestIdleCallback(load, { timeout: 1500 });
        } else {
          this.#timer = setTimeout(load, 300);
        }
      });
    });
  }

  async #loadRecommendations(priority = 'auto') {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
    if (recommendationsPerformed === 'true' || !this.isConnected) return;
    if (!productId || !sectionId || !this.dataset.url) {
      this.#activeFetch?.abort();
      this.#activeFetch = null;
      this.#pendingUrl = null;
      this.#requestVersion += 1;
      this.#handleError(new Error('Product ID, section ID and recommendation URL are required'));
      return;
    }

    const url = new URL(this.dataset.url, window.location.origin);
    url.searchParams.set('product_id', productId);
    url.searchParams.set('section_id', sectionId);
    if (intent) url.searchParams.set('intent', intent);
    const key = url.toString();
    if (this.#pendingUrl === key) return;

    this.#activeFetch?.abort();
    const controller = new AbortController();
    this.#activeFetch = controller;
    this.#pendingUrl = key;
    const version = ++this.#requestVersion;

    try {
      let html = this.#cachedRecommendations.get(key);
      if (html === undefined) {
        const response = await fetch(key, { signal: controller.signal, priority });
        if (!response.ok) throw new Error(`Server returned ${response.status}`);
        html = await response.text();
      }
      if (controller.signal.aborted || version !== this.#requestVersion || !this.isConnected) return;
      if (!html.trim()) throw new Error('No recommendations available');

      if (this.#cachedRecommendations.size >= 8) {
        this.#cachedRecommendations.delete(this.#cachedRecommendations.keys().next().value);
      }
      this.#cachedRecommendations.set(key, html);
      this.dataset.recommendationsPerformed = 'true';
      morphSection(sectionId, html, { mode: 'hydration', injectStylesheet: true });
    } catch (error) {
      if (controller.signal.aborted || version !== this.#requestVersion) return;
      if (!Shopify.designMode) this.#handleError(error);
    } finally {
      if (this.#activeFetch === controller) {
        this.#activeFetch = null;
        this.#pendingUrl = null;
      }
    }
  }

  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}

